import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAtomValue } from 'jotai';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import { CouponEditor } from './payment/coupon-editor';
import { PaymentBenefitsSection } from './payment/payment-benefits';
import { PaymentSummary } from './payment/payment-summary';
import { PlanRecurrenceOptions } from './payment/plan-recurrence-options';

import { hasExpiredPlanAtom, userPlanAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { useLoanDiscount } from 'app/hooks/use-loan-discount';
import { env } from 'app/lib/env';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { isFreeTrialPlanCode, useCreateOnePlan, useCreatePaymentSheet, useMyActivePlans } from 'app/network/plan';
import { useUser, useUserCurrency } from 'app/network/user';
import { PlanRecurrenceEnum } from 'app/schemas/plan';
import {
  buildPaymentReturnUrl,
  calculatePaymentTotal,
  getPaymentIntentAction,
  isWebPaymentEligible,
  pollForPaidPlan,
} from 'app/screens/profile/payment.web.helpers';
import { navigateInCurrentNavigatorWithFallback, navigateToPaymentSuccess } from 'app/screens/root-stack-helper';
import { buildError as planBuildError } from 'app/screens/shared/activation-modal.utils';
import { createTheme as createDSTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import type { PaymentSheet } from 'app/types/plan';
import { isNotWhiteLabel } from 'app/utils/helpers';
import { isLoanDiscountActive } from 'app/utils/loan-discount';
import { usePageTitle } from 'app/utils/web-helpers';

const theme = createDSTheme();
const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY);
const LOAN_DISCOUNT_COUPON = 'PARTAGE25';
const PLAN_POLL_INTERVAL_MS = 1000;
const PLAN_POLL_ATTEMPTS = 30;

interface PaymentContentProps {
  embedded?: boolean;
  onSuccess?: () => void;
  promoCode?: string;
}

function PaymentContentLayout({ children, embedded }: { children: ReactNode; embedded: boolean }) {
  const content = (
    <Column alignItems="center" padding="spacing.general.x4">
      <Column gap="spacing.general.x4" style={{ maxWidth: 640, width: '100%' }}>
        {children}
      </Column>
    </Column>
  );
  if (embedded) return <ScrollView style={{ flex: 1 }}>{content}</ScrollView>;
  return <Page>{content}</Page>;
}

function MobileRedirect({ embedded }: { embedded: boolean }) {
  const { t } = useScopedTranslation();
  return (
    <PaymentContentLayout embedded={embedded}>
      <Text fontSize={18} textAlign="center" lineHeight={24} style={{ maxWidth: 400 }}>
        {t('mobileRedirect.subtitle', { ns: 'payment' })}
      </Text>
    </PaymentContentLayout>
  );
}

function getPaymentErrorMessage(error: { message?: string } | undefined, fallback: string) {
  return error?.message ?? fallback;
}

function StripePaymentForm({
  disabled,
  onPaymentIntent,
}: {
  disabled: boolean;
  onPaymentIntent: (paymentIntent: PaymentIntent) => Promise<void>;
}) {
  const { t } = useScopedTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    if (!stripe || !elements || disabled) return;
    setError(undefined);
    setIsSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: buildPaymentReturnUrl(window.location.href) },
        redirect: 'if_required',
      });
      if (result.error) {
        setError(getPaymentErrorMessage(result.error, t('web.genericError', { ns: 'payment' })));
      } else {
        await onPaymentIntent(result.paymentIntent);
      }
    } catch {
      setError(t('web.genericError', { ns: 'payment' }));
    }
    setIsSubmitting(false);
  };

  return (
    <Column gap="spacing.general.x4">
      <Text fontWeight="700" variant="bodyBase">
        {t('web.paymentDetails', { ns: 'payment' })}
      </Text>
      <PaymentElement options={{ layout: 'accordion' }} />
      {error ? <RemoteError message={error} /> : null}
      <Button
        disabled={disabled || !stripe || !elements}
        loading={isSubmitting}
        onPress={() => void submit()}
        variant="primary"
      >
        {t('payButton', { ns: 'payment' })}
      </Button>
    </Column>
  );
}

export function PaymentContent({ embedded = false, onSuccess, promoCode }: PaymentContentProps) {
  const { t } = useScopedTranslation();
  const navigation = useNavigation();
  const isCorePartageClub = isNotWhiteLabel() && !env.IS_PARTAKE;
  const userCurrency = useUserCurrency();
  const { refetch: refetchUser } = useUser();
  const { data: plans, isPending: isPendingPlans, refetch: refetchPlans } = useMyActivePlans();
  const activePlan = plans?.[0];
  const isEligible = isWebPaymentEligible(activePlan?.code.code, isCorePartageClub);
  const [returnedClientSecret] = useState(
    () => new URLSearchParams(window.location.search).get('payment_intent_client_secret') ?? undefined,
  );

  const [subtotal, setSubtotal] = useState<PaymentSheet['subtotal']>();
  const [amount, setAmount] = useState<number>();
  const [clientSecret, setClientSecret] = useState(returnedClientSecret);
  const [coupon, setCoupon] = useState<string | null>(promoCode ?? null);
  const [couponFieldValue, setCouponFieldValue] = useState(promoCode ?? '');
  const [couponError, setCouponError] = useState<string>();
  const [autoCouponApplied, setAutoCouponApplied] = useState(false);
  const [paymentError, setPaymentError] = useState<string>();
  const [isResolvingPaymentIntent, setIsResolvingPaymentIntent] = useState(Boolean(returnedClientSecret));
  const [isWaitingForPlan, setIsWaitingForPlan] = useState(false);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [activationPending, setActivationPending] = useState(false);
  const [paymentFlowStarted, setPaymentFlowStarted] = useState(Boolean(returnedClientSecret));
  const paymentHandled = useRef(false);
  const paymentSheetRequestId = useRef(0);

  const hasExpiredPlan = useAtomValue(hasExpiredPlanAtom);
  const currentPlan = useAtomValue(userPlanAtom)?.[0];
  const { discountDeadline } = useLoanDiscount();
  const hasExpiredRealPlan = hasExpiredPlan && Boolean(currentPlan && !isFreeTrialPlanCode(currentPlan.code.code));
  const hasActiveLoanDiscount = !hasExpiredRealPlan && isLoanDiscountActive(discountDeadline);
  const effectiveCoupon =
    coupon ?? (hasActiveLoanDiscount && promoCode === undefined && !autoCouponApplied ? LOAN_DISCOUNT_COUPON : null);
  const showLoanDiscount = hasActiveLoanDiscount && effectiveCoupon === LOAN_DISCOUNT_COUPON;
  const [, setHasSubscriptionEndedPaywallAlreadyBeenSeen] = useStoredValue<boolean>(
    StorageKey.SubscriptionEndedPaywall,
    false,
  );

  // This callback chain must stay stable so the return-URL effect does not restart payment resolution.
  const finishSuccess = useCallback(async () => {
    setHasSubscriptionEndedPaywallAlreadyBeenSeen(false);
    await Promise.all([refetchUser(), refetchPlans()]);
    if (onSuccess) onSuccess();
    else navigateToPaymentSuccess();
  }, [onSuccess, refetchPlans, refetchUser, setHasSubscriptionEndedPaywallAlreadyBeenSeen]);

  const waitForPaidPlan = useCallback(async () => {
    setIsWaitingForPlan(true);
    const fetchPaidPlanCode = async () => {
      const result = await refetchPlans();
      return result.data?.[0]?.code.code;
    };
    try {
      const activated = await pollForPaidPlan(fetchPaidPlanCode, PLAN_POLL_ATTEMPTS, PLAN_POLL_INTERVAL_MS);
      if (activated) {
        await finishSuccess();
        return;
      }
    } catch {
      // The payment has succeeded. Fall through to the recoverable activation state
      // rather than allowing another payment attempt after a refresh failure.
    }
    setActivationPending(true);
    setIsWaitingForPlan(false);
  }, [finishSuccess, refetchPlans]);

  const handlePaymentIntent = useCallback(
    async (paymentIntent: PaymentIntent) => {
      if (paymentHandled.current) return;
      const action = getPaymentIntentAction(paymentIntent.status);
      setIsResolvingPaymentIntent(false);
      if (action === 'payment-pending') {
        setPaymentFlowStarted(true);
        paymentHandled.current = true;
        setAmount(paymentIntent.amount);
        setPaymentError(undefined);
        setIsPaymentPending(true);
        return;
      }
      if (action === 'wait-for-plan') {
        setPaymentFlowStarted(true);
        paymentHandled.current = true;
        setAmount(paymentIntent.amount);
        setPaymentError(undefined);
        await waitForPaidPlan();
        return;
      }
      if (action === 'retry-payment-method') {
        setPaymentError(paymentIntent.last_payment_error?.message ?? t('web.genericError', { ns: 'payment' }));
        setIsWaitingForPlan(false);
        return;
      }
      setPaymentError(t('web.genericError', { ns: 'payment' }));
      setIsWaitingForPlan(false);
    },
    [t, waitForPaidPlan],
  );

  const {
    mutate: createPlan,
    error: createPlanError,
    isPending: isCreatingPlan,
  } = useCreateOnePlan({
    onSuccess: () => void finishSuccess(),
    onError: () => setPaymentFlowStarted(false),
  });
  const { mutate: createPaymentSheet, isPending: isCreatingPaymentSheet } = useCreatePaymentSheet();

  if (hasActiveLoanDiscount && promoCode === undefined && !autoCouponApplied && coupon === null) {
    setCoupon(LOAN_DISCOUNT_COUPON);
    setCouponFieldValue(LOAN_DISCOUNT_COUPON);
    setAutoCouponApplied(true);
  }

  const paymentRequestInputs = [
    createPaymentSheet,
    effectiveCoupon,
    isCorePartageClub,
    isEligible,
    isPendingPlans,
    paymentFlowStarted,
    returnedClientSecret,
    t,
  ];
  const [previousPaymentRequestInputs, setPreviousPaymentRequestInputs] = useState(paymentRequestInputs);
  if (paymentRequestInputs.some((input, index) => input !== previousPaymentRequestInputs[index])) {
    setPreviousPaymentRequestInputs(paymentRequestInputs);
    if (isCorePartageClub && !isPendingPlans && isEligible && !returnedClientSecret && !paymentFlowStarted) {
      setClientSecret(undefined);
      setSubtotal(undefined);
      setAmount(undefined);
    }
  }

  useEffect(() => {
    if (!isCorePartageClub || isPendingPlans || !isEligible || returnedClientSecret || paymentFlowStarted) {
      paymentSheetRequestId.current += 1;
      return;
    }
    const requestId = paymentSheetRequestId.current + 1;
    paymentSheetRequestId.current = requestId;
    createPaymentSheet(
      { coupon: effectiveCoupon, recurrence: PlanRecurrenceEnum.yearly, source: 'web' },
      {
        onSuccess: (paymentSheet) => {
          if (paymentSheetRequestId.current !== requestId) return;
          setSubtotal(paymentSheet.subtotal);
          setAmount(
            paymentSheet.paymentIntent?.amount ??
              calculatePaymentTotal(
                paymentSheet.subtotal.discountedAmount ?? paymentSheet.subtotal.originalAmount,
                paymentSheet.subtotal.taxesPercentage,
              ),
          );
          setClientSecret(paymentSheet.paymentIntent?.client_secret);
          setCouponError(undefined);
          setPaymentError(undefined);
        },
        onError: (statusCode) => {
          if (paymentSheetRequestId.current !== requestId) return;
          setCouponError(
            statusCode === 404 ? t('invalidCoupon', { ns: 'payment' }) : t('expiredCoupon', { ns: 'payment' }),
          );
        },
      },
    );
  }, [
    createPaymentSheet,
    effectiveCoupon,
    isCorePartageClub,
    isEligible,
    isPendingPlans,
    paymentFlowStarted,
    returnedClientSecret,
    t,
  ]);

  useEffect(() => {
    if (!isCorePartageClub || isPendingPlans || isEligible || embedded || returnedClientSecret || paymentFlowStarted)
      return;
    navigateInCurrentNavigatorWithFallback(navigation, 'my-plan', 'profile', undefined, true);
  }, [embedded, isCorePartageClub, isEligible, isPendingPlans, navigation, paymentFlowStarted, returnedClientSecret]);

  useEffect(() => {
    if (!returnedClientSecret || !isCorePartageClub) return;
    void stripePromise.then(async (stripe) => {
      if (!stripe) {
        setPaymentError(t('web.genericError', { ns: 'payment' }));
        setIsResolvingPaymentIntent(false);
        setIsWaitingForPlan(false);
        return;
      }
      try {
        const result = await stripe.retrievePaymentIntent(returnedClientSecret);
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.hash}`);
        if (!result.paymentIntent) {
          // Stripe can omit both values at runtime despite the SDK's discriminated result type.
          setPaymentError(getPaymentErrorMessage(result.error, t('web.genericError', { ns: 'payment' })));
          setIsResolvingPaymentIntent(false);
          setIsWaitingForPlan(false);
          return;
        }
        await handlePaymentIntent(result.paymentIntent);
      } catch {
        setPaymentError(t('web.genericError', { ns: 'payment' }));
        setIsResolvingPaymentIntent(false);
        setIsWaitingForPlan(false);
      }
    });
  }, [handlePaymentIntent, isCorePartageClub, returnedClientSecret, t]);

  if (!isCorePartageClub) return <MobileRedirect embedded={embedded} />;
  if (isPendingPlans)
    return (
      <PaymentContentLayout embedded={embedded}>
        <Text textAlign="center">{t('web.loading', { ns: 'payment' })}</Text>
      </PaymentContentLayout>
    );
  if (!isEligible && !returnedClientSecret && !paymentFlowStarted)
    return embedded ? (
      <PaymentContentLayout embedded>
        <Text textAlign="center">{t('web.unavailable', { ns: 'payment' })}</Text>
      </PaymentContentLayout>
    ) : null;

  const stripeOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          variables: {
            colorPrimary: theme.colors['surface.brand.default'],
            borderRadius: `${String(theme.borderRadii['radius.lg'])}px`,
          },
        },
      }
    : undefined;

  const paymentInProgress = isResolvingPaymentIntent || isWaitingForPlan || isPaymentPending || activationPending;
  const statusTitle = isResolvingPaymentIntent
    ? t('web.verifyingPayment', { ns: 'payment' })
    : isPaymentPending
      ? t('web.paymentPending', { ns: 'payment' })
      : t('web.paymentConfirmed', { ns: 'payment' });
  const statusDescription = isResolvingPaymentIntent
    ? t('web.verifyingPaymentDetails', { ns: 'payment' })
    : isPaymentPending
      ? t('web.paymentPendingDetails', { ns: 'payment' })
      : t(activationPending ? 'web.activationPending' : 'web.processing', { ns: 'payment' });

  return (
    <PaymentContentLayout embedded={embedded}>
      <PlanRecurrenceOptions currency={userCurrency} discounted={showLoanDiscount} />
      <CouponEditor
        coupon={coupon}
        error={couponError}
        value={couponFieldValue}
        onChange={setCouponFieldValue}
        disabled={paymentInProgress}
        isPending={isCreatingPaymentSheet}
        onApply={() => {
          setAutoCouponApplied(false);
          setCoupon(couponFieldValue || null);
          setCouponError(undefined);
        }}
        onClear={() => {
          setAutoCouponApplied(coupon === LOAN_DISCOUNT_COUPON);
          setCoupon(null);
          setCouponFieldValue('');
          setCouponError(undefined);
        }}
      />
      <PaymentSummary
        amount={amount}
        coupon={coupon}
        isPending={isCreatingPaymentSheet}
        subtotal={subtotal}
        userCurrency={userCurrency}
      />
      <Separator color={theme.colors['surface.tertiary-neutral']} height={1} />
      <Text variant="bodyBase">{t('cancellationPolicy', { ns: 'payment' })}</Text>
      <PaymentBenefitsSection />
      {paymentError ? <RemoteError message={paymentError} /> : null}
      {paymentInProgress ? (
        <Column
          alignItems="center"
          backgroundColor="surface.brand.secondary-5.subtle"
          borderRadius="radius.lg"
          gap="spacing.general.x3"
          padding="spacing.general.x4"
        >
          {isResolvingPaymentIntent || isWaitingForPlan ? (
            <ActivityIndicator color={theme.colors['surface.brand.default']} size="large" />
          ) : null}
          <Text fontWeight="700" textAlign="center" variant="bodyBase">
            {statusTitle}
          </Text>
          <Text color="text.secondary" textAlign="center">
            {statusDescription}
          </Text>
          {isPaymentPending || activationPending ? (
            <Button
              onPress={() => navigateInCurrentNavigatorWithFallback(navigation, 'my-plan', 'profile', undefined, true)}
              variant="primary"
            >
              {t('web.viewSubscription', { ns: 'payment' })}
            </Button>
          ) : null}
        </Column>
      ) : null}
      {!paymentInProgress && amount === 0 && coupon ? (
        <Column gap="spacing.general.x3">
          {createPlanError ? planBuildError(t, createPlanError) : null}
          <Button
            loading={isCreatingPlan}
            onPress={() => {
              setPaymentFlowStarted(true);
              createPlan({ code: coupon });
            }}
            variant="primary"
          >
            {t('activateButton', { ns: 'payment' })}
          </Button>
        </Column>
      ) : null}
      {!paymentInProgress && amount !== 0 && stripeOptions ? (
        <Elements key={clientSecret} options={stripeOptions} stripe={stripePromise}>
          <StripePaymentForm disabled={isCreatingPaymentSheet} onPaymentIntent={handlePaymentIntent} />
        </Elements>
      ) : null}
      {!clientSecret && amount !== 0 && !couponError && !paymentInProgress ? (
        <Text textAlign="center">{t('web.loading', { ns: 'payment' })}</Text>
      ) : null}
    </PaymentContentLayout>
  );
}

const PaymentScreenComponent: NavigationScreen<'payment'> = ({ route }) => {
  const navigation = useNavigation();
  const { t } = useScopedTranslation();
  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text color="white-label.header.on-header" variant="bodyXLargeMedium">
        {t('title', { ns: 'payment' })}
      </Text>
    ),
  });
  const setPageTitle = usePageTitle('payment');
  useEffect(() => setPageTitle('title'), [setPageTitle]);
  return <PaymentContent promoCode={route.params?.promoCode} />;
};

export const PaymentScreen: NavigationScreen<'payment'> = withErrorBoundary(PaymentScreenComponent);
